import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { api } from '@/lib/api'

interface CheckoutFormProps {
  orderId: string
  paymentId: string
  amount: number
  customerEmail: string
  customerName: string
}

export function CheckoutForm({
  orderId,
  paymentId,
  amount,
  customerEmail,
  customerName
}: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()

  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      // Confirm the payment on Stripe
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?orderId=${orderId}`,
          receipt_email: customerEmail,
        },
        redirect: 'if_required',
      })

      if (error) {
        // Payment failed
        setErrorMessage(error.message || 'Pagamento fallito. Riprova.')
        setIsProcessing(false)
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded!
        setPaymentSuccess(true)

        // Confirm payment on backend AND submit order to kitchen
        try {
          // Step 1: Confirm payment
          await api.post('/api/payments/public/confirm', {
            paymentIntentId: paymentIntent.id
          })

          // Step 2: Submit order to kitchen (now that payment is confirmed)
          await api.post(`/api/public/order/${orderId}/submit`)

          // Redirect to success page
          setTimeout(() => {
            navigate(`/payment-success?orderId=${orderId}&paymentId=${paymentId}`)
          }, 1500)

        } catch (backendError) {
          console.error('Error confirming payment on backend:', backendError)
          // Payment succeeded on Stripe but backend confirmation failed
          // Still redirect to success page as payment went through
          setTimeout(() => {
            navigate(`/payment-success?orderId=${orderId}&paymentId=${paymentId}`)
          }, 1500)
        }
      }
    } catch (err: any) {
      console.error('Payment error:', err)
      setErrorMessage('Si è verificato un errore. Riprova.')
      setIsProcessing(false)
    }
  }

  if (paymentSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-green-900 mb-2">Pagamento Riuscito!</h3>
        <p className="text-green-700 mb-4">Il tuo pagamento è stato elaborato con successo.</p>
        <div className="flex items-center justify-center gap-2 text-green-600">
          <Loader2 className="animate-spin" size={16} />
          <span className="text-sm">Reindirizzamento in corso...</span>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Customer Info Display */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm space-y-1">
          <div><span className="font-medium">Nome:</span> {customerName}</div>
          <div><span className="font-medium">Email:</span> {customerEmail}</div>
          <div><span className="font-medium">Importo:</span> <span className="text-lg font-bold text-blue-600">€{amount.toFixed(2)}</span></div>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div>
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card', 'google_pay', 'apple_pay'],
          }}
        />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-semibold text-red-900 mb-1">Errore nel Pagamento</h4>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="animate-spin" size={24} />
            <span>Elaborazione pagamento...</span>
          </>
        ) : (
          <>
            <span>Paga €{amount.toFixed(2)}</span>
          </>
        )}
      </button>

      {/* Payment Info */}
      <div className="text-center text-xs text-gray-500">
        <p>
          Cliccando su "Paga", accetti i nostri{' '}
          <a href="#" className="text-blue-600 hover:underline">Termini di Servizio</a>
          {' '}e{' '}
          <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
        </p>
        <p className="mt-2">
          Pagamento sicuro elaborato da <span className="font-semibold">Stripe</span>
        </p>
      </div>
    </form>
  )
}

export default CheckoutForm
